import {
  HeatmapBooking,
  Booking,
  CreditCard,
  Metadata,
  Note,
} from '@deliveries-app/models';
import { AxiosError } from 'axios';
import api from '../../api';
import {
  IndexFilters,
  PostBooking,
  BookingNote,
  ChangeStatus,
  Hold,
  Reschedule,
  ProofOfDeliveryPayload,
} from '../models/booking-request.interface';
import {
  BookingResponse,
  GetAllBookingsResponse,
  NoteResponse,
  GetBookingsResponse,
  GetAllHeatmapBookingsResponse,
} from '../models/booking-response.interface';
import { processFilterParams } from '../utils/filterParams';

/**
 *
 *
 * TODO NOTES
 *
 * This service needs to match the new API format
 *
 *
 * The new API format is:
 * Base route: /api/v1/branches/:branch_id/bookings
 *
 * getAllBookings: GET /api/v1/branches/:branch_id/bookings
 * createBooking: POST /api/v1/branches/:branch_id/bookings/create
 * findDuration: POST /api/v1/branches/:branch_id/bookings/actions/find-duration
 * getBookingById: GET /api/v1/branches/:branch_id/bookings/:id
 * updateBookingById: PUT /api/v1/branches/:branch_id/bookings/:id
 * deleteBookingById: DELETE /api/v1/branches/:branch_id/bookings/:id
 * rescheduleBookingById: POST /api/v1/branches/:branch_id/bookings/:id/actions/reschedule
 * cancelBookingById: POST /api/v1/branches/:branchgit_id/bookings/:id/actions/cancel
 * holdBookingById: POST /api/v1/branches/:branch_id/bookings/:id/actions/hold
 * changeStatusBookingById: POST /api/v1/branches/:branch_id/bookings/:id/actions/change-status
 * uploadDeliveryConfirmation: POST /api/v1/branches/:branch_id/bookings/:id/actions/upload-delivery-confirmation
 *
 * getBookingNotesById: GET /api/v1/branches/:branch_id/bookings/:id/notes
 * createBookingNoteById: POST /api/v1/branches/:branch_id/bookings/:id/notes
 * getBookingNoteById: GET /api/v1/branches/:branch_id/bookings/:id/notes/:note_id
 * updateBookingNoteById: PUT /api/v1/branches/:branch_id/bookings/:id/notes/:note_id
 * deleteBookingNoteById: DELETE /api/v1/branches/:branch_id/bookings/:id/notes/:note_id
 *
 *
 *
 * Please update all the functions below to match the new API format
 * Each method MUST have:
 * 1. A try/catch block
 * 2. A console.error() statement
 * 3. A comment with correct param and response typings
 * See the getAllBookings function below for an example
 *
 *
 * IGNORE THESE ROUTES AS CHRIS NEEDS TO MIGRATE
 * /delivered-image
 * /proof-of-deliveries
 * Ignore all heatmaps related routes also.
 *
 *
 *
 * On completion there will be a number of methods that are no longer needed
 * If you think they are still required, let Chris know and we will make a plan
 */

/**
 * Get all bookings by branch
 * @param {number} branch_id
 *
 * @returns {Promise<{data: HeatmapBooking[]; _metadata: Metadata;}>}
 */
export async function getAllHeatmapBookings(
  branch_id: number,
  filters?: IndexFilters
): Promise<{
  data: HeatmapBooking[];
  _metadata: Metadata;
}> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });

    const res = await api.get<GetAllHeatmapBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/heatmap`,
      {
        params,
      }
    );

    if (!res.data.success) {
      throw new Error('Get all heatmap bookings failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get all heatmap bookings failed', err);
    throw err;
  }
}

/**
 * Get all bookings by branch
 * @param {number} branch_id
 *
 * @returns {Promise<{data: Booking[]; _metadata: Metadata;}>}
 */
export async function getAllBookings(
  branch_id: number,
  filters?: IndexFilters
): Promise<{
  data: Booking[];
  _metadata: Metadata;
}> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });

    const res = await api.get<GetAllBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings`,
      {
        params,
      }
    );

    if (!res.data.success) {
      throw new Error('Get all bookings failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get all bookings failed', err);
    throw err;
  }
}

/**
 * Get all bookings by branch
 * @param {number} branch_id
 *
 * @returns {Promise<{data: Booking[]; _metadata: Metadata;}>}
 */
export async function getDraftBookings(
  branch_id: number,
  filters?: IndexFilters
): Promise<{
  data: Booking[];
  _metadata: Metadata;
}> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });

    const res = await api.get<GetAllBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/draft`,
      {
        params,
      }
    );

    if (!res.data.success) {
      throw new Error('Get draft bookings failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get draft bookings failed', err);
    throw err;
  }
}

/**
 * Get all bookings with time status by branch
 * @param {number} branch_id
 *
 * @returns {Promise<{data: Booking[]; _metadata: Metadata;}>}
 */
export async function getAllBookingsWithTimeStatus(
  branch_id: number,
  filters?: IndexFilters
): Promise<{
  data: Booking[];
  _metadata: Metadata;
}> {
  try {
    const params = processFilterParams({ filters: filters ?? {} });

    const res = await api.get<GetAllBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/time-status`,
      {
        params,
      }
    );

    if (!res.data.success) {
      throw new Error('Get all bookings with time status failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get all booking with time status failed', err);
    throw err;
  }
}

/**
 * Delete booking by Id
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: Booking;}>}
 */

export async function deleteBookingById(
  branch_id: number,
  booking_id: number
): Promise<{
  data: Booking;
}> {
  try {
    const res = await api.delete<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}`
    );

    if (!res.data.success) {
      throw new Error('Delete booking failed');
    }

    return res.data;
  } catch (err) {
    console.log('Delete booking failed', err);
    throw err;
  }
}

/**
 * Get Driver All Booking
 * @param {number} branch_id
 * @param {number} driver_id
 *
 * @returns {Promise<{data: Booking;}>}
 */

export async function getDriverAllBooking(
  branch_id: number,
  driver_id: number | undefined,
  date: string
): Promise<{
  data: Booking[];
}> {
  try {
    const res = await api.get<GetAllBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings?start_date=${date}&end_date=${date}&completed=false${
        driver_id ? `&driver_id=${driver_id}` : ''
      }`
    );

    if (!res.data.success) {
      throw new Error('Error Get All Bookings');
    }

    return res.data;
  } catch (err: any) {
    console.log('Get All Bookings', err);

    if (err?.response?.data?.message) {
      console.log('Error response:', err.response);
    }
    throw err;
  }
}

/**
 * Get Booking By Id
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: Booking;}>}
 */
export async function getBookingById(
  branch_id: number,
  booking_id: number
): Promise<Booking> {
  try {
    const res = await api.get<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}`
    );

    if (!res.data.success) {
      throw new Error('Get booking failed');
    }

    return res.data.data;
  } catch (err) {
    console.log('Get booking failed', err);
    throw err;
  }
}

/**
 * Create New Booking
 * @param {number} branch_id
 * @param {PostBooking} param
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function createBooking(
  branch_id: number,
  param: PostBooking
): Promise<GetBookingsResponse> {
  try {
    const res = await api.post<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/create`,
      param
    );

    if (!res.data.success) {
      throw new Error('Create booking failed');
    }

    return res.data;
  } catch (err: any) {
    if (err.response?.data) {
      let values: string[] = [];

      if (err.response.data.errors) {
        const errors = err.response.data.errors;

        Object.entries(errors)
          .map(([key, value]) => value as string[])
          .forEach((value: string[]) => {
            console.log(...value);
            values = [...values, ...value];
          });
      }

      throw {
        error: err.response.data.message,
        errors: values,
      };
    }

    throw err;
  }
}

/**
 * Update Booking By Id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {PostBooking} param
 *
 * @returns {Promise<GetBookingsResponse>}
 */
export async function updateBookingById(
  branch_id: number,
  booking_id: number,
  param: PostBooking
): Promise<GetBookingsResponse> {
  try {
    const res = await api.put<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}`,
      param
    );

    if (!res.data.success) {
      throw new Error('Update booking failed');
    }

    return res.data;
  } catch (err: any) {
    console.log('Update booking failed', err);

    if (err.response?.data) {
      let values: string[] = [];

      if (err.response.data.errors) {
        const errors = err.response.data.errors;

        Object.entries(errors)
          .map(([key, value]) => value as string[])
          .forEach((value: string[]) => {
            console.log(...value);
            values = [...values, ...value];
          });
      }

      throw {
        error: err.response.data.message,
        errors: values,
      };
    }
    throw err;
  }
}

/**
 * Cancel Booking By Id
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function cancelBooking(
  branch_id: number,
  bookingId: number
): Promise<{
  data: Booking;
}> {
  try {
    const res = await api.post<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${bookingId}/actions/cancel`
    );

    if (!res.data.success) {
      throw new Error('Update booking failed');
    }

    return res.data;
  } catch (err) {
    console.log('Cancel booking failed', err);
    throw err;
  }
}

/**
 * Reschedule booking by id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {Reschedule} booking
 *
 * @returns {Promise<GetBookingsResponse>}
 */
export async function rescheduleBookingById(
  branch_id: number,
  booking_id: number,
  data: Reschedule
): Promise<GetBookingsResponse> {
  try {
    const res = await api.post<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/reschedule`,
      data
    );

    if (!res.data.success) {
      throw new Error('Reschedule booking failed');
    }

    return res.data;
  } catch (err: any) {
    console.log('Reschedule booking failed', err);

    if (err.response?.data) {
      let values: string[] = [];

      if (err.response.data.errors) {
        const errors = err.response.data.errors;

        Object.entries(errors)
          .map(([key, value]) => value as string[])
          .forEach((value: string[]) => {
            console.log(...value);
            values = [...values, ...value];
          });
      }

      throw {
        error: err.response.data.message,
        errors: values,
      };
    }
    throw err;
  }
}

/**
 * Hold Booking By Id
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function holdBookingById(
  branch_id: number,
  booking_id: number
): Promise<{
  data: Booking;
}> {
  try {
    const res = await api.put<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/hold`
    );

    if (!res.data.success) {
      throw new Error('Hold booking failed');
    }

    return res.data;
  } catch (err) {
    console.log('Hold booking failed', err);
    throw err;
  }
}

/**
 * Change Status Booking By Id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {ChangeStatus} param
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function changeStatusBookingById(
  branch_id: number,
  booking_id: number,
  param: ChangeStatus
): Promise<{
  data: Booking;
}> {
  try {
    const res = await api.post<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/change-status`,
      param
    );

    if (!res.data.success) {
      throw new Error('Change status booking failed');
    }

    return res.data;
  } catch (err) {
    console.log('Change status booking failed', err);
    throw err;
  }
}

/**
 * Get Booking Notes By Booking ID
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: Note}>}
 */
export async function getBookingNotesById(
  branch_id: number,
  booking_id: number
): Promise<{
  data: Note;
}> {
  try {
    const res = await api.get<NoteResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/notes`
    );

    if (!res.data.success) {
      throw new Error('Get booking notes failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get booking notes failed', err);
    throw err;
  }
}

/**
 * Add Booking Notes By Booking ID
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {BookingNote} param
 *
 * @returns {Promise<{data: NoteResponse}>}
 */
export async function addBookingNotesById(
  branch_id: number,
  booking_id: number,
  param: BookingNote
): Promise<Note> {
  try {
    const res = await api.post<NoteResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/notes`,
      param
    );

    if (!res.data.success) {
      throw new Error('Add booking notes failed');
    }

    return res.data.data;
  } catch (err) {
    console.log('Add booking notes failed', err);
    throw err;
  }
}

/**
 * Get Booking Note By Id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {number} note_id
 *
 * @returns {Promise<{data: Note}>}
 */
export async function getBookingNoteById(
  branch_id: number,
  booking_id: number,
  note_id: number
): Promise<{
  data: Booking;
}> {
  try {
    const res = await api.get<NoteResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/notes/${note_id}`
    );

    if (!res.data.success) {
      throw new Error('Get note failed');
    }

    return res.data;
  } catch (err) {
    console.log('Get note failed', err);
    throw err;
  }
}

/**
 * Update Booking Note By Id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {number} note_id
 * @param {UpdateNote} param
 *
 * @returns {Promise<{data: Note}>}
 */
export async function updateBookingNoteById(
  branch_id: number,
  booking_id: number,
  note_id: number,
  param: BookingNote
): Promise<{
  data: Note;
}> {
  try {
    const res = await api.put<NoteResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/notes/${note_id}`,
      param
    );

    if (!res.data.success) {
      throw new Error('Update note failed');
    }

    return res.data;
  } catch (err) {
    console.log('Update note failed', err);
    throw err;
  }
}

/**
 * Delete Booking Note By Id
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {number} note_id
 *
 * @returns {Promise<{data: Note }>}
 */
export async function deleteBookingNoteById(
  branch_id: number,
  booking_id: number,
  note_id: number
): Promise<{
  data: Note;
}> {
  try {
    const res = await api.delete<NoteResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/notes/${note_id}`
    );

    if (!res.data.success) {
      throw new Error('Delete note failed');
    }

    return res.data;
  } catch (err) {
    console.log('Delete note failed', err);
    throw err;
  }
}

/**
 * Find estimated booking time for lat lng and start time
 *
 * @param {number} branch_id
 * @param {string} lat
 * @param {string} lng
 * @param {Date} start_time
 *
 * @returns {Promise<{duration: number; warning?: string}>}
 */
export async function findEstimatedBookingTime(
  branch_id: number,
  lat: string,
  lng: string,
  start_time: Date
): Promise<{ duration: number; warning?: string }> {
  try {
    const start_time_str = start_time
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');

    const res = await api.post(
      `/api/v1/branches/${branch_id}/bookings/actions/find-duration?latitude=${lat}&longitude=${lng}&start_time=${start_time_str}`
    );

    if (!res.data.success) {
      throw new Error('Find estimated booking time failed');
    }

    return {
      duration: res.data.data.duration,
      ...(res.data.warning && { warning: res.data.warning }),
    };
  } catch (err) {
    console.log('Find estimated booking time failed', err);
    throw err;
  }
}

/**
 * Access encrypted credit card data for booking
 *
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<{data: CreditCard}>}
 */
export async function accessCreditCardData(
  branch_id: number,
  booking_id: number
): Promise<CreditCard | null> {
  try {
    const res = await api.get<{
      success: boolean;
      data: CreditCard;
    }>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/fetch-credit-card`
    );

    if (!res.data.success) {
      throw new Error('Access credit card data failed');
    }

    if (res.data.data.number === undefined) {
      return null;
    }

    return res.data.data as CreditCard;
  } catch (err) {
    console.log('Access credit card data failed', err);
    throw err;
  }
}

/**
 * Send Delivered Image by booking
 *  @param {number} branch_id
 *  @param {number} booking_id
 *  @param {string} image
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function postDeliveredImage(
  branch_id: number,
  booking_id: number,
  image: { image: string }
): Promise<{ data: Booking }> {
  try {
    const res = await api.post<GetBookingsResponse>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/upload-delivery-confirmation-image`,
      image
    );
    if (!res.data.success) {
      throw new Error('Post Image failed');
    }

    return res.data;
  } catch (err) {
    console.log('Error when post image', err);
    throw err;
  }
}

/**
 * Upload delivery confirmation image
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {FormData} param
 *
 * @returns {Promise<{data: Booking}>}
 */
export async function storeProofOfDelivery(
  branch_id: number,
  booking_id: number,
  payload: ProofOfDeliveryPayload
): Promise<{ data: Booking }> {
  try {
    const res = await api.post<{ data: Booking }>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/store-proof-of-delivery`,
      payload
    );

    return res.data;
  } catch (err) {
    console.log('Get current booking request failed', err);
    throw err;
  }
}

/**
 * Update booking SMS setting
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {boolean} sms
 *
 * @returns {Promise<Booking>}
 */
export async function updateBookingSmsSetting(
  branch_id: number,
  booking_id: number,
  sms: boolean
): Promise<Booking> {
  try {
    const res = await api.post<{ data: Booking }>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/update-sms`,
      {
        sms,
      }
    );

    return res.data.data;
  } catch (err) {
    console.log('update sms booking failed', err);
    throw err;
  }
}

/**
 * Update driver set to booking
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {number} driver_id
 *
 * @returns {Promise<Booking>}
 */
export async function updateBookingDriverById(
  branch_id: number,
  booking_id: number,
  driver_id: number
): Promise<Booking> {
  try {
    const res = await api.post<{ data: Booking }>(
      `/api/v1/branches/${branch_id}/bookings/${booking_id}/actions/update-driver`,
      {
        driver_id,
      }
    );

    return res.data.data;
  } catch (err) {
    console.log('Update driver request failed', err);
    throw err;
  }
}

export const bookingService = {
  getAllBookings,
  getDraftBookings,
  getAllHeatmapBookings,
  getAllBookingsWithTimeStatus,
  deleteBookingById,
  rescheduleBookingById,
  holdBookingById,
  changeStatusBookingById,
  getBookingNotesById,
  addBookingNotesById,
  getBookingNoteById,
  updateBookingNoteById,
  deleteBookingNoteById,
  getBookingById,
  createBooking,
  updateBookingById,
  cancelBooking,
  getDriverAllBooking,
  postDeliveredImage,
  findEstimatedBookingTime,
  accessCreditCardData,
  updateBookingSmsSetting,
  updateBookingDriverById,
  storeProofOfDelivery,
};
